import { useState } from 'react';
import { useAdminAuth } from '../../../hooks/useAdminAuth';

interface Plan {
  name: string;
  price: string;
  annualPrice: string;
  period: string;
  annualPeriod: string;
  features: string[];
}

interface CheckoutModalProps {
  plan: Plan;
  billingCycle: 'monthly' | 'annual';
  onClose: () => void;
}

type Step = 'account' | 'payment' | 'success';

const PLAN_COLORS: Record<string, string> = {
  Free: 'from-emerald-500 to-teal-500',
  Pro: 'from-violet-600 to-cyan-500',
  'Golden Pro': 'from-gray-800 to-gray-600',
};

const PLAN_ICONS: Record<string, string> = {
  Free: 'ri-seedling-line',
  Pro: 'ri-rocket-line',
  'Golden Pro': 'ri-building-4-line',
};

export default function CheckoutModal({ plan, billingCycle, onClose }: CheckoutModalProps) {
  const { isAdmin, user: adminUser } = useAdminAuth();

  const [step, setStep] = useState<Step>('account');
  const [isLoading, setIsLoading] = useState(false);

  // Account fields
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [agreeTerms, setAgreeTerms] = useState(false);

  // Payment fields
  const [cardNumber, setCardNumber] = useState('');
  const [cardExpiry, setCardExpiry] = useState('');
  const [cardCvc, setCardCvc] = useState('');
  const [cardName, setCardName] = useState('');
  const [promoCode, setPromoCode] = useState('');
  const [promoApplied, setPromoApplied] = useState(false);
  const [promoError, setPromoError] = useState('');

  // Errors
  const [accountErrors, setAccountErrors] = useState<Record<string, string>>({});
  const [paymentErrors, setPaymentErrors] = useState<Record<string, string>>({});

  // ── Admin bypass: render admin success screen instead ────────────────────
  if (isAdmin) {
    const gradient = PLAN_COLORS[plan.name] ?? 'from-amber-500 to-yellow-400';
    return (
      <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose}></div>
        <div
          className="relative bg-white rounded-3xl shadow-2xl w-full max-w-md overflow-hidden"
          style={{ animation: 'checkoutSlideIn 0.3s cubic-bezier(0.34,1.56,0.64,1)' }}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Golden header */}
          <div className="bg-gradient-to-r from-amber-500 via-yellow-500 to-amber-400 px-8 py-7">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 flex items-center justify-center rounded-2xl bg-white/20 backdrop-blur-sm flex-shrink-0">
                  <i className="ri-vip-crown-2-fill text-white text-xl"></i>
                </div>
                <div>
                  <p className="text-white/80 text-xs font-semibold uppercase tracking-widest">Admin Access</p>
                  <h2 className="text-white text-xl font-extrabold leading-tight">Already Unlocked!</h2>
                </div>
              </div>
              <button
                onClick={onClose}
                className="w-9 h-9 flex items-center justify-center rounded-full bg-white/20 hover:bg-white/30 transition-colors cursor-pointer flex-shrink-0"
              >
                <i className="ri-close-line text-white text-lg"></i>
              </button>
            </div>
          </div>

          {/* Body */}
          <div className="px-8 py-7 flex flex-col items-center gap-5">
            <div className="w-20 h-20 flex items-center justify-center rounded-full bg-gradient-to-br from-amber-400 to-yellow-400 shadow-xl">
              <i className="ri-shield-star-fill text-white text-3xl"></i>
            </div>
            <div className="text-center">
              <h3 className="text-xl font-extrabold text-gray-900 mb-2">No Payment Needed</h3>
              <p className="text-sm text-gray-500 leading-relaxed">
                You&apos;re signed in as <strong className="text-amber-600">{adminUser?.email}</strong> with full
                <span className="text-amber-600 font-bold"> Golden Pro</span> admin access.<br />
                All features are already unlocked — no credit card required.
              </p>
            </div>
            <div className="w-full flex flex-col gap-2">
              {[
                { icon: 'ri-infinity-line', label: 'Unlimited generations — no limits ever' },
                { icon: 'ri-tools-line', label: 'All 11 AI tools fully unlocked' },
                { icon: 'ri-shield-user-line', label: 'Full admin rights active' },
                { icon: 'ri-vip-crown-line', label: 'Golden Pro — every feature included' },
                { icon: 'ri-bank-card-line', label: 'No credit card — 100% free admin access' },
              ].map(item => (
                <div key={item.label} className="flex items-center gap-3 p-3 rounded-xl bg-amber-50 border border-amber-100">
                  <div className="w-7 h-7 flex items-center justify-center rounded-lg bg-amber-400 flex-shrink-0">
                    <i className={`${item.icon} text-white text-sm`}></i>
                  </div>
                  <span className="text-sm font-semibold text-gray-800">{item.label}</span>
                  <i className="ri-check-line text-amber-500 text-sm ml-auto flex-shrink-0"></i>
                </div>
              ))}
            </div>
            <button
              onClick={onClose}
              className="w-full py-3.5 bg-gradient-to-r from-amber-500 to-yellow-400 text-white text-sm font-bold rounded-full hover:shadow-xl hover:scale-[1.02] transition-all cursor-pointer whitespace-nowrap flex items-center justify-center gap-2"
            >
              <i className="ri-sparkling-line text-base"></i>
              Start Creating — Full Access Active
            </button>
          </div>
        </div>
        <style>{`
          @keyframes checkoutSlideIn {
            from { opacity: 0; transform: scale(0.92) translateY(20px); }
            to   { opacity: 1; transform: scale(1) translateY(0); }
          }
        `}</style>
      </div>
    );
  }

  const isFree = plan.name === 'Free Trial';
  const displayPrice = billingCycle === 'annual' ? plan.annualPrice : plan.price;
  const displayPeriod = billingCycle === 'annual' ? plan.annualPeriod : plan.period;
  const finalPrice = promoApplied && !isFree
    ? `$${Math.round(parseFloat(displayPrice.replace('$', '')) * 0.8)}`
    : displayPrice;

  // ── Formatters ──────────────────────────────────────────────────────────────
  const formatCardNumber = (val: string) => {
    const digits = val.replace(/\D/g, '').slice(0, 16);
    return digits.replace(/(.{4})/g, '$1 ').trim();
  };

  const formatExpiry = (val: string) => {
    const digits = val.replace(/\D/g, '').slice(0, 4);
    if (digits.length >= 3) return `${digits.slice(0, 2)} / ${digits.slice(2)}`;
    return digits;
  };

  const getCardBrand = () => {
    const d = cardNumber.replace(/\s/g, '');
    if (d.startsWith('4')) return 'ri-visa-line';
    if (/^5[1-5]/.test(d)) return 'ri-mastercard-line';
    if (d.startsWith('3')) return 'ri-bank-card-line';
    return 'ri-bank-card-line';
  };

  // ── Validation ──────────────────────────────────────────────────────────────
  const validateAccount = () => {
    const errs: Record<string, string> = {};
    if (!fullName.trim()) errs.fullName = 'Full name is required';
    if (!email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) errs.email = 'Valid email is required';
    if (password.length < 8) errs.password = 'Password must be at least 8 characters';
    if (!agreeTerms) errs.terms = 'You must agree to the terms';
    setAccountErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const validatePayment = () => {
    const errs: Record<string, string> = {};
    if (!cardName.trim()) errs.cardName = 'Cardholder name is required';
    const digits = cardNumber.replace(/\s/g, '');
    if (digits.length < 16) errs.cardNumber = 'Enter a valid 16-digit card number';
    if (cardExpiry.replace(/\s/g, '').length < 5) errs.cardExpiry = 'Enter a valid expiry date';
    if (cardCvc.length < 3) errs.cardCvc = 'Enter a valid CVC';
    setPaymentErrors(errs);
    return Object.keys(errs).length === 0;
  };

  // ── Handlers ─────────────────────────────────────────────────────────────────
  const handleAccountSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateAccount()) return;

    if (isFree) {
      // Submit form for free plan
      setIsLoading(true);
      try {
        const body = new URLSearchParams();
        body.append('fullName', fullName);
        body.append('email', email);
        body.append('plan', plan.name);
        body.append('billingCycle', billingCycle);
        await fetch('https://readdy.ai/api/form/d6oqjdv5ne2i7jjrvr40', {
          method: 'POST',
          headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
          body: body.toString(),
        });
        setStep('success');
      } finally {
        setIsLoading(false);
      }
    } else {
      setStep('payment');
    }
  };

  const handlePaymentSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validatePayment()) return;
    setIsLoading(true);
    try {
      const body = new URLSearchParams();
      body.append('fullName', fullName);
      body.append('email', email);
      body.append('plan', plan.name);
      body.append('billingCycle', billingCycle);
      body.append('price', finalPrice);
      body.append('promoApplied', promoApplied ? 'STAY20' : 'none');
      await fetch('https://readdy.ai/api/form/d6oqjdv5ne2i7jjrvr40', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });
      setStep('success');
    } finally {
      setIsLoading(false);
    }
  };

  const handleApplyPromo = () => {
    if (promoCode.trim().toUpperCase() === 'STAY20') {
      setPromoApplied(true);
      setPromoError('');
    } else {
      setPromoError('Invalid promo code. Try STAY20');
      setPromoApplied(false);
    }
  };

  const gradient = PLAN_COLORS[plan.name] ?? 'from-violet-600 to-cyan-500';
  const planIcon = PLAN_ICONS[plan.name] ?? 'ri-rocket-line';

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/60 backdrop-blur-sm"
        onClick={onClose}
      ></div>

      {/* Modal */}
      <div
        className="relative bg-white rounded-3xl shadow-2xl w-full max-w-2xl overflow-hidden flex flex-col max-h-[92vh]"
        style={{ animation: 'checkoutSlideIn 0.3s cubic-bezier(0.34,1.56,0.64,1)' }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* ── Header ── */}
        <div className={`bg-gradient-to-r ${gradient} px-8 py-6 flex-shrink-0`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 flex items-center justify-center rounded-2xl bg-white/20 backdrop-blur-sm flex-shrink-0">
                <i className={`${planIcon} text-white text-xl`}></i>
              </div>
              <div>
                <p className="text-white/80 text-xs font-semibold uppercase tracking-widest">
                  {step === 'success' ? 'All Done!' : step === 'payment' ? 'Secure Checkout' : isFree ? 'Create Your Free Account' : 'Get Started'}
                </p>
                <h2 className="text-white text-xl font-extrabold leading-tight">
                  {step === 'success'
                    ? `Welcome to openclaw.ai!`
                    : isFree && step === 'account'
                    ? 'Register — No Card Needed'
                    : `${plan.name} Plan`}
                </h2>
              </div>
            </div>
            <button
              onClick={onClose}
              className="w-9 h-9 flex items-center justify-center rounded-full bg-white/20 hover:bg-white/30 transition-colors cursor-pointer flex-shrink-0"
            >
              <i className="ri-close-line text-white text-lg"></i>
            </button>
          </div>

          {/* Step indicator — only show on account/payment steps */}
          {step !== 'success' && (
            <div className="flex items-center gap-2 mt-5">
              {(['account', ...(isFree ? [] : ['payment'])] as Step[]).map((s, i, arr) => (
                <div key={s} className="flex items-center gap-2">
                  <div className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold transition-all ${
                    step === s
                      ? 'bg-white text-gray-900'
                      : arr.indexOf(step) > i
                      ? 'bg-white/30 text-white'
                      : 'bg-white/10 text-white/50'
                  }`}>
                    {arr.indexOf(step) > i
                      ? <i className="ri-check-line text-xs"></i>
                      : <span>{i + 1}</span>
                    }
                    <span className="capitalize">{s === 'account' ? 'Account' : 'Payment'}</span>
                  </div>
                  {i < arr.length - 1 && (
                    <div className={`h-0.5 w-8 rounded-full transition-all ${arr.indexOf(step) > i ? 'bg-white/60' : 'bg-white/20'}`}></div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* ── Scrollable Body ── */}
        <div className="overflow-y-auto flex-1">

          {/* ── SUCCESS STATE ── */}
          {step === 'success' && (
            <div className="flex flex-col px-8 py-7">
              {/* Top celebration */}
              <div className="flex flex-col items-center text-center mb-6">
                <div className={`w-20 h-20 flex items-center justify-center rounded-full bg-gradient-to-br ${gradient} shadow-2xl mb-4`}>
                  <i className={`${isFree ? 'ri-seedling-line' : 'ri-check-line'} text-white text-4xl`}></i>
                </div>
                <h3 className="text-2xl font-extrabold text-gray-900 mb-1.5">
                  {isFree ? "You're in! Welcome 🎉" : 'Payment Successful!'}
                </h3>
                <p className="text-gray-500 text-sm leading-relaxed max-w-sm">
                  {isFree
                    ? 'Your free account is ready. You have 1,500 free words to explore — no credit card needed.'
                    : `Your ${plan.name} plan is now active. A confirmation has been sent to ${email}.`}
                </p>
                <div className="flex items-center gap-2 px-4 py-2 bg-emerald-50 border border-emerald-200 rounded-full mt-3">
                  <i className="ri-mail-check-line text-emerald-500 text-base"></i>
                  <span className="text-emerald-700 text-sm font-semibold">{email}</span>
                </div>
              </div>

              {/* Free trial: tool access + usage meter */}
              {isFree ? (
                <>
                  {/* Usage allowance */}
                  <div className="bg-gradient-to-r from-emerald-50 to-teal-50 border border-emerald-200 rounded-2xl p-4 mb-4">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <div className="w-6 h-6 flex items-center justify-center rounded-lg bg-emerald-500 flex-shrink-0">
                          <i className="ri-file-word-line text-white text-xs"></i>
                        </div>
                        <span className="text-sm font-bold text-gray-800">Your free word allowance</span>
                      </div>
                      <span className="text-sm font-extrabold text-emerald-700">1,500 words</span>
                    </div>
                    <div className="w-full h-2 rounded-full bg-emerald-200 overflow-hidden mb-1.5">
                      <div className="h-full w-0 rounded-full bg-gradient-to-r from-emerald-400 to-teal-400"></div>
                    </div>
                    <p className="text-xs text-emerald-600 font-medium">0 / 1,500 words used · Upgrade anytime for unlimited access</p>
                  </div>

                  {/* 3 accessible tools */}
                  <div className="mb-5">
                    <p className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-3">3 Tools included in your free plan</p>
                    <div className="grid grid-cols-3 gap-2.5">
                      {[
                        { name: 'Paper Writer', icon: 'ri-file-text-line', color: 'from-violet-500 to-violet-700', desc: 'Essays & research papers', path: '/paper-writer' },
                        { name: 'Resume Builder', icon: 'ri-profile-line', color: 'from-orange-500 to-amber-600', desc: 'ATS-optimized resumes', path: '/resume-builder' },
                        { name: 'Genius Math', icon: 'ri-calculator-line', color: 'from-emerald-500 to-teal-600', desc: 'Step-by-step solutions', path: '/genius-math' },
                      ].map((tool) => (
                        <div key={tool.name} className="flex flex-col items-center gap-2 p-3 rounded-xl bg-gray-50 border border-gray-100 text-center">
                          <div className={`w-10 h-10 flex items-center justify-center rounded-xl bg-gradient-to-br ${tool.color} flex-shrink-0`}>
                            <i className={`${tool.icon} text-white text-base`}></i>
                          </div>
                          <div>
                            <p className="text-xs font-bold text-gray-800 leading-tight">{tool.name}</p>
                            <p className="text-xs text-gray-400 leading-tight mt-0.5">{tool.desc}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                    <div className="mt-2 flex items-center gap-2 px-3 py-2 rounded-xl bg-amber-50 border border-amber-200">
                      <i className="ri-lock-line text-amber-500 text-sm flex-shrink-0"></i>
                      <p className="text-xs text-amber-700 font-medium">Upgrade to Pro to unlock all 10 tools, unlimited generations &amp; no watermarks</p>
                    </div>
                  </div>

                  {/* First generation CTA */}
                  <div className="mb-4 p-4 rounded-2xl border-2 border-dashed border-violet-200 bg-violet-50/50">
                    <p className="text-xs font-bold text-violet-700 uppercase tracking-widest mb-2">Suggested first generation</p>
                    <p className="text-sm font-semibold text-gray-700 mb-1">✍️ Write a research paper on a topic you care about</p>
                    <p className="text-xs text-gray-400 leading-relaxed">Open Paper Writer, describe your topic in a few sentences, and let the AI do the rest. Takes about 30 seconds.</p>
                  </div>
                </>
              ) : (
                /* Paid plan: show all features */
                <div className="bg-gray-50 rounded-2xl p-5 mb-5 text-left">
                  <p className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-3">What you get</p>
                  <div className="grid grid-cols-2 gap-2">
                    {plan.features.slice(0, 6).map((f, i) => (
                      <div key={i} className="flex items-center gap-2">
                        <i className="ri-check-line text-emerald-500 text-sm flex-shrink-0"></i>
                        <span className="text-xs font-medium text-gray-700">{f}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <button
                onClick={onClose}
                className={`w-full py-4 bg-gradient-to-r ${gradient} text-white text-sm font-bold rounded-full hover:shadow-xl hover:scale-[1.02] transition-all whitespace-nowrap cursor-pointer flex items-center justify-center gap-2`}
              >
                <i className="ri-sparkling-line text-base"></i>
                {isFree ? 'Start My First Generation' : 'Start Creating Now'}
                <i className="ri-arrow-right-line text-base"></i>
              </button>

              {isFree && (
                <p className="text-center text-xs text-gray-400 mt-3">
                  Want unlimited access?{' '}
                  <button onClick={onClose} className="text-violet-600 font-bold hover:underline cursor-pointer">
                    Upgrade to Pro →
                  </button>
                </p>
              )}
            </div>
          )}

          {/* ── ACCOUNT STEP ── */}
          {step === 'account' && (
            <form
              data-readdy-form
              onSubmit={handleAccountSubmit}
              className="px-8 py-6 space-y-5"
            >
              {/* Plan summary pill */}
              <div className={`flex items-center justify-between px-4 py-3 rounded-2xl bg-gradient-to-r ${gradient} bg-opacity-10`}
                style={{ background: isFree ? 'linear-gradient(to right, rgba(16,185,129,0.08), rgba(20,184,166,0.08))' : 'linear-gradient(to right, rgba(139,92,246,0.07), rgba(6,182,212,0.07))' }}>
                <div className="flex items-center gap-2">
                  <div className={`w-8 h-8 flex items-center justify-center rounded-xl bg-gradient-to-br ${gradient} flex-shrink-0`}>
                    <i className={`${planIcon} text-white text-sm`}></i>
                  </div>
                  <div>
                    <p className="text-sm font-bold text-gray-900">{plan.name} Plan</p>
                    {isFree
                      ? <p className="text-xs text-emerald-600 font-semibold flex items-center gap-1"><i className="ri-check-line text-xs"></i>No credit card required</p>
                      : <p className="text-xs text-gray-500">{displayPeriod}</p>
                    }
                  </div>
                </div>
                <div className="text-right">
                  <p className={`text-lg font-extrabold ${isFree ? 'text-emerald-600' : 'text-gray-900'}`}>{isFree ? '$0' : displayPrice}</p>
                  {!isFree && billingCycle === 'annual' && plan.name === 'Pro' && (
                    <p className="text-xs text-emerald-600 font-semibold">Save $240/yr</p>
                  )}
                  {!isFree && billingCycle === 'annual' && plan.name === 'Golden Pro' && (
                    <p className="text-xs text-emerald-600 font-semibold">Save $120/yr</p>
                  )}
                </div>
              </div>

              {/* Social sign-up */}
              <div className="grid grid-cols-2 gap-3">
                <button
                  type="button"
                  className="flex items-center justify-center gap-2 px-4 py-3 rounded-xl border border-gray-200 bg-white hover:bg-gray-50 text-sm font-semibold text-gray-700 transition-all cursor-pointer"
                >
                  <i className="ri-google-fill text-base" style={{ color: '#4285F4' }}></i>
                  Continue with Google
                </button>
                <button
                  type="button"
                  className="flex items-center justify-center gap-2 px-4 py-3 rounded-xl border border-gray-200 bg-white hover:bg-gray-50 text-sm font-semibold text-gray-700 transition-all cursor-pointer"
                >
                  <i className="ri-github-fill text-base text-gray-900"></i>
                  Continue with GitHub
                </button>
              </div>

              <div className="flex items-center gap-3">
                <div className="flex-1 h-px bg-gray-200"></div>
                <span className="text-xs font-semibold text-gray-400">or sign up with email</span>
                <div className="flex-1 h-px bg-gray-200"></div>
              </div>

              {/* Full Name */}
              <div>
                <label className="block text-xs font-bold text-gray-600 uppercase tracking-widest mb-1.5">Full Name</label>
                <div className="relative">
                  <i className="ri-user-line absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 text-base"></i>
                  <input
                    name="fullName"
                    type="text"
                    value={fullName}
                    onChange={(e) => setFullName(e.target.value)}
                    placeholder="Jane Smith"
                    className={`w-full pl-11 pr-4 py-3 rounded-xl border text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-2 transition-all ${
                      accountErrors.fullName
                        ? 'border-red-300 focus:ring-red-200'
                        : 'border-gray-200 focus:ring-violet-200 focus:border-violet-400'
                    }`}
                  />
                </div>
                {accountErrors.fullName && (
                  <p className="text-xs text-red-500 mt-1 flex items-center gap-1">
                    <i className="ri-error-warning-line text-xs"></i>{accountErrors.fullName}
                  </p>
                )}
              </div>

              {/* Email */}
              <div>
                <label className="block text-xs font-bold text-gray-600 uppercase tracking-widest mb-1.5">Email Address</label>
                <div className="relative">
                  <i className="ri-mail-line absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 text-base"></i>
                  <input
                    name="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="jane@example.com"
                    className={`w-full pl-11 pr-4 py-3 rounded-xl border text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-2 transition-all ${
                      accountErrors.email
                        ? 'border-red-300 focus:ring-red-200'
                        : 'border-gray-200 focus:ring-violet-200 focus:border-violet-400'
                    }`}
                  />
                </div>
                {accountErrors.email && (
                  <p className="text-xs text-red-500 mt-1 flex items-center gap-1">
                    <i className="ri-error-warning-line text-xs"></i>{accountErrors.email}
                  </p>
                )}
              </div>

              {/* Password — for all plans */}
              {(
                <div>
                  <label className="block text-xs font-bold text-gray-600 uppercase tracking-widest mb-1.5">Password</label>
                  <div className="relative">
                    <i className="ri-lock-line absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 text-base"></i>
                    <input
                      name="password"
                      type={showPassword ? 'text' : 'password'}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="Min. 8 characters"
                      className={`w-full pl-11 pr-12 py-3 rounded-xl border text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-2 transition-all ${
                        accountErrors.password
                          ? 'border-red-300 focus:ring-red-200'
                          : 'border-gray-200 focus:ring-violet-200 focus:border-violet-400'
                      }`}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(v => !v)}
                      className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 cursor-pointer"
                    >
                      <i className={`${showPassword ? 'ri-eye-off-line' : 'ri-eye-line'} text-base`}></i>
                    </button>
                  </div>
                  {/* Password strength */}
                  {password.length > 0 && (
                    <div className="mt-2 flex items-center gap-2">
                      <div className="flex gap-1 flex-1">
                        {[1, 2, 3, 4].map(i => (
                          <div
                            key={i}
                            className={`h-1 flex-1 rounded-full transition-all ${
                              password.length >= i * 3
                                ? password.length >= 12
                                  ? 'bg-emerald-500'
                                  : password.length >= 8
                                  ? 'bg-amber-400'
                                  : 'bg-red-400'
                                : 'bg-gray-200'
                            }`}
                          ></div>
                        ))}
                      </div>
                      <span className={`text-xs font-semibold ${
                        password.length >= 12 ? 'text-emerald-600' : password.length >= 8 ? 'text-amber-600' : 'text-red-500'
                      }`}>
                        {password.length >= 12 ? 'Strong' : password.length >= 8 ? 'Good' : 'Weak'}
                      </span>
                    </div>
                  )}
                  {accountErrors.password && (
                    <p className="text-xs text-red-500 mt-1 flex items-center gap-1">
                      <i className="ri-error-warning-line text-xs"></i>{accountErrors.password}
                    </p>
                  )}
                </div>
              )}

              {/* Terms */}
              <div>
                <label className="flex items-start gap-3 cursor-pointer group">
                  <div className="relative flex-shrink-0 mt-0.5">
                    <input
                      type="checkbox"
                      checked={agreeTerms}
                      onChange={(e) => setAgreeTerms(e.target.checked)}
                      className="sr-only"
                    />
                    <div
                      className={`w-5 h-5 rounded-md border-2 flex items-center justify-center transition-all ${
                        agreeTerms ? 'bg-violet-600 border-violet-600' : 'border-gray-300 group-hover:border-violet-400'
                      }`}
                    >
                      {agreeTerms && <i className="ri-check-line text-white text-xs"></i>}
                    </div>
                  </div>
                  <span className="text-sm text-gray-600 leading-relaxed">
                    I agree to the{' '}
                    <a href="#" className="text-violet-600 hover:underline font-semibold">Terms of Service</a>
                    {' '}and{' '}
                    <a href="#" className="text-violet-600 hover:underline font-semibold">Privacy Policy</a>
                  </span>
                </label>
                {accountErrors.terms && (
                  <p className="text-xs text-red-500 mt-1 flex items-center gap-1 ml-8">
                    <i className="ri-error-warning-line text-xs"></i>{accountErrors.terms}
                  </p>
                )}
              </div>

              <button
                type="submit"
                disabled={isLoading}
                className={`w-full py-4 bg-gradient-to-r ${gradient} text-white text-sm font-bold rounded-full hover:shadow-xl hover:scale-[1.02] transition-all whitespace-nowrap cursor-pointer flex items-center justify-center gap-2 disabled:opacity-70 disabled:cursor-not-allowed`}
              >
                {isLoading ? (
                  <><i className="ri-loader-4-line animate-spin text-base"></i>Creating account…</>
                ) : isFree ? (
                  <><i className="ri-sparkling-line text-base"></i>Create Free Account</>
                ) : (
                  <><span>Continue to Payment</span><i className="ri-arrow-right-line text-base"></i></>
                )}
              </button>

              <p className="text-center text-xs text-gray-400">
                Already have an account?{' '}
                <a href="#" className="text-violet-600 hover:underline font-semibold">Sign in</a>
              </p>
            </form>
          )}

          {/* ── PAYMENT STEP ── */}
          {step === 'payment' && (
            <form
              data-readdy-form
              onSubmit={handlePaymentSubmit}
              className="px-8 py-6 space-y-5"
            >
              {/* Order summary */}
              <div className="bg-gray-50 rounded-2xl p-4 space-y-2">
                <p className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-3">Order Summary</p>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-600">{plan.name} Plan ({billingCycle})</span>
                  <span className="font-semibold text-gray-900">{displayPrice}</span>
                </div>
                {promoApplied && (
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-emerald-600 flex items-center gap-1">
                      <i className="ri-coupon-line text-sm"></i>Promo STAY20 (20% off)
                    </span>
                    <span className="font-semibold text-emerald-600">
                      -${Math.round(parseFloat(displayPrice.replace('$', '')) * 0.2)}
                    </span>
                  </div>
                )}
                <div className="flex items-center justify-between pt-2 border-t border-gray-200">
                  <span className="text-sm font-bold text-gray-900">Total</span>
                  <span className="text-lg font-extrabold text-gray-900">{finalPrice}<span className="text-xs font-normal text-gray-500"> / {displayPeriod}</span></span>
                </div>
              </div>

              {/* Promo code */}
              <div>
                <label className="block text-xs font-bold text-gray-600 uppercase tracking-widest mb-1.5">Promo Code</label>
                <div className="flex gap-2">
                  <div className="relative flex-1">
                    <i className="ri-coupon-line absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 text-base"></i>
                    <input
                      name="promoCode"
                      type="text"
                      value={promoCode}
                      onChange={(e) => { setPromoCode(e.target.value.toUpperCase()); setPromoError(''); }}
                      placeholder="e.g. STAY20"
                      disabled={promoApplied}
                      className={`w-full pl-11 pr-4 py-3 rounded-xl border text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-2 transition-all ${
                        promoApplied
                          ? 'border-emerald-300 bg-emerald-50 text-emerald-700'
                          : promoError
                          ? 'border-red-300 focus:ring-red-200'
                          : 'border-gray-200 focus:ring-violet-200 focus:border-violet-400'
                      }`}
                    />
                  </div>
                  <button
                    type="button"
                    onClick={handleApplyPromo}
                    disabled={promoApplied || !promoCode.trim()}
                    className={`px-5 py-3 rounded-xl text-sm font-bold transition-all whitespace-nowrap cursor-pointer ${
                      promoApplied
                        ? 'bg-emerald-100 text-emerald-700 cursor-default'
                        : 'bg-gray-900 text-white hover:bg-gray-700 disabled:opacity-40 disabled:cursor-not-allowed'
                    }`}
                  >
                    {promoApplied ? <><i className="ri-check-line mr-1"></i>Applied</> : 'Apply'}
                  </button>
                </div>
                {promoError && (
                  <p className="text-xs text-red-500 mt-1 flex items-center gap-1">
                    <i className="ri-error-warning-line text-xs"></i>{promoError}
                  </p>
                )}
                {promoApplied && (
                  <p className="text-xs text-emerald-600 mt-1 flex items-center gap-1 font-semibold">
                    <i className="ri-check-double-line text-xs"></i>20% discount applied!
                  </p>
                )}
              </div>

              {/* Card details */}
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <label className="text-xs font-bold text-gray-600 uppercase tracking-widest">Card Details</label>
                  <div className="flex items-center gap-2">
                    <i className="ri-shield-check-fill text-emerald-500 text-sm"></i>
                    <span className="text-xs text-gray-400 font-medium">256-bit SSL Encrypted</span>
                  </div>
                </div>

                {/* Cardholder name */}
                <div>
                  <div className="relative">
                    <i className="ri-user-line absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 text-base"></i>
                    <input
                      name="cardName"
                      type="text"
                      value={cardName}
                      onChange={(e) => setCardName(e.target.value)}
                      placeholder="Cardholder name"
                      className={`w-full pl-11 pr-4 py-3 rounded-xl border text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-2 transition-all ${
                        paymentErrors.cardName
                          ? 'border-red-300 focus:ring-red-200'
                          : 'border-gray-200 focus:ring-violet-200 focus:border-violet-400'
                      }`}
                    />
                  </div>
                  {paymentErrors.cardName && (
                    <p className="text-xs text-red-500 mt-1 flex items-center gap-1">
                      <i className="ri-error-warning-line text-xs"></i>{paymentErrors.cardName}
                    </p>
                  )}
                </div>

                {/* Card number */}
                <div>
                  <div className="relative">
                    <i className="ri-bank-card-line absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 text-base"></i>
                    <input
                      name="cardNumber"
                      type="text"
                      value={cardNumber}
                      onChange={(e) => setCardNumber(formatCardNumber(e.target.value))}
                      placeholder="1234 5678 9012 3456"
                      maxLength={19}
                      className={`w-full pl-11 pr-12 py-3 rounded-xl border text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-2 transition-all font-mono tracking-wider ${
                        paymentErrors.cardNumber
                          ? 'border-red-300 focus:ring-red-200'
                          : 'border-gray-200 focus:ring-violet-200 focus:border-violet-400'
                      }`}
                    />
                    {cardNumber.length > 0 && (
                      <div className="absolute right-4 top-1/2 -translate-y-1/2">
                        <i className={`${getCardBrand()} text-gray-400 text-lg`}></i>
                      </div>
                    )}
                  </div>
                  {paymentErrors.cardNumber && (
                    <p className="text-xs text-red-500 mt-1 flex items-center gap-1">
                      <i className="ri-error-warning-line text-xs"></i>{paymentErrors.cardNumber}
                    </p>
                  )}
                </div>

                {/* Expiry + CVC */}
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <div className="relative">
                      <i className="ri-calendar-line absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 text-base"></i>
                      <input
                        name="cardExpiry"
                        type="text"
                        value={cardExpiry}
                        onChange={(e) => setCardExpiry(formatExpiry(e.target.value))}
                        placeholder="MM / YY"
                        maxLength={7}
                        className={`w-full pl-11 pr-4 py-3 rounded-xl border text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-2 transition-all font-mono ${
                          paymentErrors.cardExpiry
                            ? 'border-red-300 focus:ring-red-200'
                            : 'border-gray-200 focus:ring-violet-200 focus:border-violet-400'
                        }`}
                      />
                    </div>
                    {paymentErrors.cardExpiry && (
                      <p className="text-xs text-red-500 mt-1 flex items-center gap-1">
                        <i className="ri-error-warning-line text-xs"></i>{paymentErrors.cardExpiry}
                      </p>
                    )}
                  </div>
                  <div>
                    <div className="relative">
                      <i className="ri-lock-line absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 text-base"></i>
                      <input
                        name="cardCvc"
                        type="text"
                        value={cardCvc}
                        onChange={(e) => setCardCvc(e.target.value.replace(/\D/g, '').slice(0, 4))}
                        placeholder="CVC"
                        maxLength={4}
                        className={`w-full pl-11 pr-4 py-3 rounded-xl border text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-2 transition-all font-mono ${
                          paymentErrors.cardCvc
                            ? 'border-red-300 focus:ring-red-200'
                            : 'border-gray-200 focus:ring-violet-200 focus:border-violet-400'
                        }`}
                      />
                    </div>
                    {paymentErrors.cardCvc && (
                      <p className="text-xs text-red-500 mt-1 flex items-center gap-1">
                        <i className="ri-error-warning-line text-xs"></i>{paymentErrors.cardCvc}
                      </p>
                    )}
                  </div>
                </div>
              </div>

              {/* Trust badges */}
              <div className="flex items-center justify-center gap-5 py-2">
                {[
                  { icon: 'ri-shield-check-fill', text: 'Secure', color: 'text-emerald-500' },
                  { icon: 'ri-refund-2-line', text: '30-day refund', color: 'text-violet-500' },
                  { icon: 'ri-lock-fill', text: 'Encrypted', color: 'text-cyan-500' },
                ].map((b) => (
                  <div key={b.text} className="flex items-center gap-1.5">
                    <div className={`w-4 h-4 flex items-center justify-center flex-shrink-0`}>
                      <i className={`${b.icon} ${b.color} text-sm`}></i>
                    </div>
                    <span className="text-xs text-gray-500 font-medium">{b.text}</span>
                  </div>
                ))}
              </div>

              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={() => setStep('account')}
                  className="flex items-center gap-2 px-5 py-4 rounded-full border border-gray-200 text-sm font-semibold text-gray-700 hover:bg-gray-50 transition-all whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-arrow-left-line text-base"></i>
                  Back
                </button>
                <button
                  type="submit"
                  disabled={isLoading}
                  className={`flex-1 py-4 bg-gradient-to-r ${gradient} text-white text-sm font-bold rounded-full hover:shadow-xl hover:scale-[1.02] transition-all whitespace-nowrap cursor-pointer flex items-center justify-center gap-2 disabled:opacity-70 disabled:cursor-not-allowed`}
                >
                  {isLoading ? (
                    <><i className="ri-loader-4-line animate-spin text-base"></i>Processing…</>
                  ) : (
                    <><i className="ri-shield-check-line text-base"></i>Pay {finalPrice} &amp; Activate</>
                  )}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>

      <style>{`
        @keyframes checkoutSlideIn {
          from { opacity: 0; transform: scale(0.92) translateY(20px); }
          to   { opacity: 1; transform: scale(1) translateY(0); }
        }
      `}</style>
    </div>
  );
}
